import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./i18n/config";
import {
  createBrowserRouter,
  redirect,
  RouterProvider,
} from "react-router-dom";
import ErrorPage from "./error-page";
import Login from "./routes/guestRoutes/login/login";
import Register from "./routes/guestRoutes/register/register";
import Courses from "./routes/courses/courses";
import Course from "@/routes/course/course";
import HomepageLayout from "./routes/homePage/homepageLayout";
import { guestLoader } from "./services/loaders/guestLoader";
import AppLayout from "./layouts/appLayout";
import { loginAction } from "./routes/guestRoutes/login/actions/loginAction";
import GuestLayout from "./layouts/guestLayout";
import { coursesLoader } from "./routes/courses/loaders/coursesLoader";
import { adminProtectedLoader } from "./services/loaders/adminProtectedLoader";
import { courseLoader } from "./routes/course/loaders/courseLoader";
import { homePageLoader } from "./routes/homePage/loaders/homePageLoader";
import { registerAction } from "@/routes/guestRoutes/register/actions/registerAction";
import { profileLoader } from "@/routes/settings/accountInfo/loaders/profileLoaders";
import { accountInfoAction } from "./routes/settings/accountInfo/actions/accountInfoAction";
import { changePasswordAction } from "@/routes/settings/password/actions/changePasswordAction.ts";
import NotificationComponent from "./components/notification";
import { authService } from "./services/auth/authService";
import { LecturerProtectedLoader } from "./services/loaders/lecturerProtectedLoader";
import { myCoursesLoader } from "@/routes/myCourses/loaders/myCoursesLoader.ts";
import { certificatesLoader } from "./routes/certificates/loaders/certificatesLoader";
import { lecturerFinanceLoader } from "./routes/lecturer/routes/finance/loaders/lecturerFinanceLoader";
import { RoleEnum } from "./types/auth/roleEnum";
import { adminPanelCategoryLoader } from "@/routes/adminPanel/categories/main/loaders/adminPanelCategoryLoader";
import { adminPanelCategoryEditLoader } from "@/routes/adminPanel/categories/edit/adminPanelCategoryEditLoader.ts";
import { adminPanelCategoryEditAction } from "@/routes/adminPanel/categories/edit/adminPanelCategoryEditAction.ts";
import { adminPanelCategoryCreateAction } from "@/routes/adminPanel/categories/create/adminPanelCategoryCreateAction.ts";
import { adminPanelUserLoader } from "@/routes/adminPanel/users/main/loaders/adminPanelUserLoader.ts";
import { adminPanelUserEditLoader } from "@/routes/adminPanel/users/edit/adminPanelUserEditLoader.ts";
import { adminPanelUserEditAction } from "@/routes/adminPanel/users/edit/adminPanelUserEditAction.ts";
import { adminPanelTagLoader } from "@/routes/adminPanel/tags/main/adminPanelTagLoader.ts";
import { adminPanelTagEditLoader } from "./routes/adminPanel/tags/edit/adminPanelTagEditLoader.ts";
import { adminPanelTagEditAction } from "@/routes/adminPanel/tags/edit/adminPanelTagEditAction.ts";
import { adminPanelTagCreateAction } from "@/routes/adminPanel/tags/create/adminPanelTagCreateAction.ts";
import { adminPanelDashboardLoader } from "@/routes/adminPanel/dashboard/adminPanelDashboardLoader.ts";
import ForgotPassword from "./routes/guestRoutes/forgotPassword/forgotPassword";
import { forgotPasswordEnterEmailAction } from "./routes/guestRoutes/forgotPassword/actions/forgotPasswordEnterEmailAction";
import VerifyCode from "./routes/guestRoutes/verifyCode/verifyCode";
import { VerifyCodeAction } from "./routes/guestRoutes/verifyCode/actions/verifyCodeAction";
import ResetPassword from "./routes/guestRoutes/resetPassword/resetPassword";
import { ResetPasswordAction } from "./routes/guestRoutes/resetPassword/actions/resetPasswordAction";
import { ManageCourseAction } from "./routes/lecturer/routes/manageCourseContent/actions/manageCourseAction.ts";
import { ManageCourseCreateSectionAction } from "./routes/lecturer/routes/manageCourseContent/actions/manageCourseCreateSectionAction.ts";
import { ManageCourseUpdateSectionAction } from "./routes/lecturer/routes/manageCourseContent/actions/manageCourseUpdateSectionAction.ts";
import { lecturerManageCoursesLoader } from "./routes/lecturer/routes/manageCourses/loaders/lecturerManageCoursesLoader.ts";
import { lecturerManageCoursesAction } from "./routes/lecturer/routes/manageCourses/actions/lecturerManageCoursesAction.ts";
import { lecturerManageCourseContentLoader } from "./routes/lecturer/routes/manageCourseContent/loaders/manageCourseContentLoader.ts";
import { lecturerCreateCourseAction } from "./routes/lecturer/routes/createCourse/actions/lecturerCreateCourseAction.ts";
import { lecturerCreateCourseLoader } from "./routes/lecturer/routes/createCourse/loaders/lecturerCreateCourseLoader.ts";
import { basketLoader } from "./routes/basket/basketLoader.ts";
import { orderSummaryLoader } from "@/routes/orders/summary/orderSummaryLoader.ts";
import { orderHistoryLoader } from "@/routes/orders/history/orderHistoryLoader.ts";
import { discountCodeLoader } from "@/routes/lecturer/routes/discountCode/main/discountCodeLoader.ts";
import { discountCodeEditLoader } from "@/routes/lecturer/routes/discountCode/edit/discountCodeEditLoader.ts";
import { discountCodeCreateAction } from "@/routes/lecturer/routes/discountCode/create/discountCodeCreateAction.ts";
import { discountCodeEditAction } from "@/routes/lecturer/routes/discountCode/edit/discountCodeEditAction.ts";
import { adminPanelDiscountCodeLoader } from "@/routes/adminPanel/discountCodes/main/adminPanelDiscountCodeLoader.ts";
import { bookmarksLoader } from "@/routes/bookmarks/bookmarksLoader.ts";
import { adminPanelDiscountCodeEditLoader } from "@/routes/adminPanel/discountCodes/edit/adminPanelDiscountCodeEditLoader.ts";
import { adminPanelDiscountCodeEditAction } from "@/routes/adminPanel/discountCodes/edit/adminPanelDiscountCodeEditAction.ts";
import { adminPanelDiscountCodeCreateAction } from "@/routes/adminPanel/discountCodes/create/adminPanelDiscountCodeCreateAction.ts";
import { ManageCourseUpdateSectionLoader } from "./routes/lecturer/routes/manageCourseContent/loaders/manageCourseUpdateSectionLoader.ts";
import { ManageCourseUpdateLectureLoader } from "./routes/lecturer/routes/manageCourseContent/loaders/manageCourseUpdateLectureLoader.ts";
import { manageCourseInfoLoader } from "./routes/lecturer/routes/manageCourseInfo/loaders/manageCourseInfoLoader.ts";
import { LectureLayout } from "./routes/course/lecture/components/lectureLayout.tsx";
import { lectureLoader } from "./routes/course/lecture/loaders/lectureLoader.ts";
import { lectureAction } from "./routes/course/lecture/actions/lectureAction.ts";
import { AdminManageCoursesLoader } from "./routes/adminPanel/manageCourses/loaders/adminManageCoursesLoader.ts";
import { AdminManageCoursesAction } from "./routes/adminPanel/manageCourses/action/adminManageCoursesAction.ts";
import { adminManageCoursesInDetailLoader } from "./routes/adminPanel/manageCoursesInDetail/manageCourses/loaders/adminManageCoursesInDetailLoader.ts";
import { adminManageCoursesInDetailAction } from "./routes/adminPanel/manageCoursesInDetail/manageCourses/actions/adminManageCoursesInDetailAction.ts";
import { adminManageCourseInfoLoader } from "./routes/adminPanel/manageCoursesInDetail/manageCourseInfo/loaders/adminManageCourseInfoLoader.ts";
import { adminManageCourseContentLoader } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/loaders/adminManageCourseContentLoader.ts";
import { AdminManageCourseAction } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/actions/adminManageCourseAction.ts";
import { AdminManageCourseCreateSectionAction } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/actions/adminManageCourseCreateSectionAction.ts";
import { AdminManageCourseUpdateSectionAction } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/actions/adminManageCourseUpdateSectionAction.ts";
import { AdminManageCourseUpdateSectionLoader } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/loaders/adminManageCourseUpdateSectionLoader.ts";
import { AdminManageCourseUpdateLectureLoader } from "./routes/adminPanel/manageCoursesInDetail/manageCourseContent/loaders/adminManageCourseUpdateLectureLoader.ts";
import { memberOnlyLoader } from "./services/loaders/memberOnlyLoader.ts";
import ProtectedRoute from "./layouts/protectedRoute.tsx";
import { lazyRoute } from "./services/helpers/routerHelpers";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

authService.initialize().then(() => {
  const router = createBrowserRouter([
  {
    element: <GuestLayout />,
    loader: guestLoader,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/login",
        element: <Login />,
        action: loginAction,
      },
      {
        path: "/register",
        element: <Register />,
        action: registerAction,
      },
      {
        path: "/forgot-password",
        element: <ForgotPassword />,
        action: forgotPasswordEnterEmailAction,
      },
      {
        path: "/verify-code",
        element: <VerifyCode />,
        action: VerifyCodeAction,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
        action: ResetPasswordAction,
      },
    ],
  },
  {
    id: "root",
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    loader: () => ({
      isAuthenticated: !!authService.getAccessToken(),
      userName: authService.userData
        ? `${authService.userData.name} ${authService.userData.surname}`
        : null,
      userRoles: authService.userData?.roles ?? null,
    }),
    children: [
      {
        path: "/",
        loader: () => {
          if (!authService.getAccessToken()) throw redirect("/homepage");
          const roles = authService.userData?.roles;
          if (roles?.includes(RoleEnum.Admin)) throw redirect("/admin-panel");
          if (roles?.includes(RoleEnum.Lecturer)) throw redirect("/lecturer");
          throw redirect("/homepage");
        },
      },
      // Public routes
      {
        path: "/homepage",
        loader: homePageLoader,
        element: <HomepageLayout />,
      },
      {
        path: "/courses",
        element: <Courses />,
        loader: coursesLoader,
      },
      {
        path: "course/:courseId",
        element: <Course />,
        loader: courseLoader,
      },
      {
        path: "/course/:courseId/:lectureId",
        loader: lectureLoader,
        action: lectureAction,
        element: <LectureLayout />,
      },
      // Member-only routes
      {
        element: <ProtectedRoute />,
        loader: memberOnlyLoader,
        children: [
          {
            path: "/my-courses",
            element: lazyRoute(() => import("./routes/myCourses/myCourses")),
            loader: myCoursesLoader,
          },
          {
            path: "/bookmarks",
            element: lazyRoute(() => import("./routes/bookmarks/bookmarks")),
            loader: bookmarksLoader,
          },
          {
            path: "/certificates",
            element: lazyRoute(
              () => import("./routes/certificates/certificates"),
            ),
            loader: certificatesLoader,
          },
          {
            path: "/basket",
            element: lazyRoute(() => import("./routes/basket/basket")),
            loader: basketLoader,
          },
          {
            path: "/order/:orderId/summary",
            element: lazyRoute(
              () => import("@/routes/orders/summary/orderSummary"),
            ),
            loader: orderSummaryLoader,
          },
          {
            path: "/orders/history",
            element: lazyRoute(
              () => import("@/routes/orders/history/orderHistory"),
            ),
            loader: orderHistoryLoader,
          },
          {
            path: "/payment/checkout/:basketId",
            element: lazyRoute(
              () => import("@/routes/payment/checkout/paymentCheckout"),
            ),
          },
          {
            path: "/payment/callback",
            element: lazyRoute(
              () => import("@/routes/payment/callback/paymentCallback"),
            ),
          },
          {
            path: "/payment/success/:orderId",
            element: lazyRoute(
              () => import("@/routes/payment/success/paymentSuccess"),
            ),
          },
          {
            path: "/payment/fail/:orderId",
            element: lazyRoute(
              () => import("@/routes/payment/fail/paymentFail"),
            ),
          },
          {
            path: "/settings",
            element: lazyRoute(() => import("./routes/settings/settings")),
            children: [
              {
                path: "",
                loader: () => {
                  return redirect("/settings/account-info");
                },
              },
              {
                path: "account-info",
                loader: profileLoader,
                element: lazyRoute(
                  () => import("./routes/settings/accountInfo/profile"),
                ),
                action: accountInfoAction,
              },
              {
                path: "change-password",
                element: lazyRoute(
                  () => import("@/routes/settings/password/changePassword"),
                ),
                action: changePasswordAction,
              },
            ],
          },
        ],
      },
      // Admin routes
      {
        path: "/admin-panel",
        loader: adminProtectedLoader,
        errorElement: <ErrorPage />,
        element: lazyRoute(() => import("./routes/adminPanel/main/adminPanel")),
        children: [
          {
            path: "",
            loader: () => {
              return redirect("/admin-panel/dashboard");
            },
          },
          {
            path: "dashboard",
            loader: adminPanelDashboardLoader,
            element: lazyRoute(
              () => import("@/routes/adminPanel/dashboard/adminPanelDashboard"),
            ),
          },
          {
            path: "manage-courses",
            loader: AdminManageCoursesLoader,
            action: AdminManageCoursesAction,
            element: lazyRoute(
              () =>
                import("./routes/adminPanel/manageCourses/components/adminManageCoursesLayout"),
            ),
          },
          {
            path: "manage-courses-in-detail",
            children: [
              {
                index: true,
                loader: adminManageCoursesInDetailLoader,
                action: adminManageCoursesInDetailAction,
                element: lazyRoute(
                  () =>
                    import("./routes/adminPanel/manageCoursesInDetail/manageCourses/adminManageCoursesInDetail"),
                ),
              },
              {
                path: ":courseId",
                children: [
                  {
                    path: "info",
                    loader: adminManageCourseInfoLoader,
                    element: lazyRoute(
                      () =>
                        import("./routes/adminPanel/manageCoursesInDetail/manageCourseInfo/adminManageCourseInfoEditLayout"),
                    ),
                  },
                  {
                    path: "content",
                    id: "admin-manage-course-content",
                    loader: adminManageCourseContentLoader,
                    action: AdminManageCourseAction,
                    element: lazyRoute(() =>
                      import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseContentLayout").then(
                        (m) => ({ default: m.AdminManageCourseContentLayout }),
                      ),
                    ),
                    children: [
                      {
                        index: true,
                        element: lazyRoute(() =>
                          import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseContent").then(
                            (m) => ({ default: m.AdminManageCourseContent }),
                          ),
                        ),
                      },
                    ],
                  },
                  {
                    path: "create-section",
                    action: AdminManageCourseCreateSectionAction,
                    element: lazyRoute(() =>
                      import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseCreateSection").then(
                        (m) => ({ default: m.AdminManageCourseCreateSection }),
                      ),
                    ),
                  },
                  {
                    path: ":sectionId/create-lecture",
                    element: lazyRoute(() =>
                      import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseCreateLecture").then(
                        (m) => ({ default: m.AdminManageCourseCreateLecture }),
                      ),
                    ),
                  },
                  {
                    path: ":sectionId/edit-section",
                    loader: AdminManageCourseUpdateSectionLoader,
                    action: AdminManageCourseUpdateSectionAction,
                    element: lazyRoute(() =>
                      import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseUpdateSection").then(
                        (m) => ({ default: m.AdminManageCourseUpdateSection }),
                      ),
                    ),
                  },
                  {
                    path: ":lectureId/edit-lecture",
                    loader: AdminManageCourseUpdateLectureLoader,
                    element: lazyRoute(() =>
                      import("./routes/adminPanel/manageCoursesInDetail/manageCourseContent/adminManageCourseUpdateLecture").then(
                        (m) => ({ default: m.AdminManageCourseUpdateLecture }),
                      ),
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "categories",
            loader: adminPanelCategoryLoader,
            element: lazyRoute(
              () =>
                import("./routes/adminPanel/categories/main/adminPanelCategory"),
            ),
          },
          {
            path: "categories/:categoryId/edit",
            loader: adminPanelCategoryEditLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/categories/edit/adminPanelCategoryEdit").then(
                (m) => ({ default: m.AdminPanelCategoryEdit }),
              ),
            ),
            action: adminPanelCategoryEditAction,
          },
          {
            path: "categories/create",
            loader: adminPanelCategoryLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/categories/create/adminPanelCategoryCreate").then(
                (m) => ({ default: m.AdminPanelCategoryCreate }),
              ),
            ),
            action: adminPanelCategoryCreateAction,
          },
          {
            path: "users",
            loader: adminPanelUserLoader,
            element: lazyRoute(
              () => import("@/routes/adminPanel/users/main/adminPanelUser"),
            ),
          },
          {
            path: "users/:userId/edit",
            loader: adminPanelUserEditLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/users/edit/adminPanelUserEdit").then(
                (m) => ({ default: m.AdminPanelUserEdit }),
              ),
            ),
            action: adminPanelUserEditAction,
          },
          {
            path: "tags",
            loader: adminPanelTagLoader,
            element: lazyRoute(
              () => import("@/routes/adminPanel/tags/main/adminPanelTag"),
            ),
          },
          {
            path: "tags/:tagId/edit",
            loader: adminPanelTagEditLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/tags/edit/adminPanelTagEdit").then(
                (m) => ({ default: m.AdminPanelTagEdit }),
              ),
            ),
            action: adminPanelTagEditAction,
          },
          {
            path: "tags/create",
            loader: adminPanelTagLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/tags/create/adminPanelTagCreate").then(
                (m) => ({ default: m.AdminPanelTagCreate }),
              ),
            ),
            action: adminPanelTagCreateAction,
          },
          {
            path: "discount-codes",
            loader: adminPanelDiscountCodeLoader,
            element: lazyRoute(
              () =>
                import("@/routes/adminPanel/discountCodes/main/adminPanelDiscountCode"),
            ),
          },
          {
            path: "discount-codes/:discountCodeId/edit",
            loader: adminPanelDiscountCodeEditLoader,
            element: lazyRoute(() =>
              import("@/routes/adminPanel/discountCodes/edit/adminPanelDiscountCodeEdit").then(
                (m) => ({ default: m.AdminPanelDiscountCodeEdit }),
              ),
            ),
            action: adminPanelDiscountCodeEditAction,
          },
          {
            path: "discount-codes/create",
            element: lazyRoute(() =>
              import("@/routes/adminPanel/discountCodes/create/adminPanelDiscountCodeCreate").then(
                (m) => ({ default: m.AdminPanelDiscountCodeCreate }),
              ),
            ),
            action: adminPanelDiscountCodeCreateAction,
          },
          {
            path: "sales-report",
            element: lazyRoute(
              () =>
                import("@/routes/adminPanel/salesReport/adminPanelSalesReport"),
            ),
          },
        ],
      },
      // Lecturer routes
      {
        path: "/lecturer",
        loader: LecturerProtectedLoader,
        children: [
          {
            path: "",
            element: lazyRoute(() => import("./routes/lecturer/lecturer")),
          },
          {
            path: "finance",
            element: lazyRoute(
              () => import("./routes/lecturer/routes/finance/lecturerFinance"),
            ),
            loader: lecturerFinanceLoader,
          },
          {
            path: "create-course",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/createCourse/lecturerCreateCourse").then(
                (m) => ({ default: m.LecturerCreateCourse }),
              ),
            ),
            action: lecturerCreateCourseAction,
            loader: lecturerCreateCourseLoader,
          },
          {
            path: "discount-codes",
            element: lazyRoute(
              () =>
                import("@/routes/lecturer/routes/discountCode/main/discountCode"),
            ),
            loader: discountCodeLoader,
          },
          {
            path: "discount-codes/create",
            element: lazyRoute(() =>
              import("@/routes/lecturer/routes/discountCode/create/discountCodeCreate").then(
                (m) => ({ default: m.DiscountCodeCreate }),
              ),
            ),
            action: discountCodeCreateAction,
          },
          {
            path: "discount-codes/:discountCodeId/edit",
            element: lazyRoute(() =>
              import("@/routes/lecturer/routes/discountCode/edit/discountCodeEdit").then(
                (m) => ({ default: m.DiscountCodeEdit }),
              ),
            ),
            loader: discountCodeEditLoader,
            action: discountCodeEditAction,
          },
          {
            path: "manage-courses",
            element: lazyRoute(
              () =>
                import("./routes/lecturer/routes/manageCourses/lecturerManageCourses"),
            ),
            loader: lecturerManageCoursesLoader,
            action: lecturerManageCoursesAction,
          },
          {
            path: "manage-course-info/:courseId/",
            element: lazyRoute(
              () =>
                import("./routes/lecturer/routes/manageCourseInfo/manageCourseInfoEditLayout"),
            ),
            loader: manageCourseInfoLoader,
          },
          {
            path: "manage-course/:courseId",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/manageCourseContent/manageCourseContentLayout").then(
                (m) => ({ default: m.ManageCourseContentLayout }),
              ),
            ),
            id: "manage-course-content",
            loader: lecturerManageCourseContentLoader,
            action: ManageCourseAction,
            children: [
              {
                index: true,
                element: lazyRoute(() =>
                  import("./routes/lecturer/routes/manageCourseContent/manageCourseContent").then(
                    (m) => ({ default: m.ManageCourseContent }),
                  ),
                ),
              },
            ],
          },
          {
            path: ":courseId/create-section",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/manageCourseContent/manageCourseCreateSection").then(
                (m) => ({ default: m.ManageCourseCreateSection }),
              ),
            ),
            action: ManageCourseCreateSectionAction,
          },
          {
            path: ":courseId/:sectionId/create-lecture",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/manageCourseContent/manageCourseCreateLecture").then(
                (m) => ({ default: m.ManageCourseCreateLecture }),
              ),
            ),
          },
          {
            path: ":courseId/:sectionId/edit-section",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/manageCourseContent/manageCourseUpdateSection").then(
                (m) => ({ default: m.ManageCourseUpdateSection }),
              ),
            ),
            action: ManageCourseUpdateSectionAction,
            loader: ManageCourseUpdateSectionLoader,
          },
          {
            path: ":courseId/:lectureId/edit-lecture",
            element: lazyRoute(() =>
              import("./routes/lecturer/routes/manageCourseContent/manageCourseUpdateLecture").then(
                (m) => ({ default: m.ManageCourseUpdateLecture }),
              ),
            ),
            loader: ManageCourseUpdateLectureLoader,
          },
        ],
      },
    ],
  },
  ]);

  createRoot(rootElement).render(
    <>
      <NotificationComponent />
      <RouterProvider router={router} />
    </>,
  );
});
